import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { useAuth } from '../context/AuthContext';
import useCollection from './useCollection';
import { 
  FaCalendarAlt, 
  FaComments, 
  FaVideo, 
  FaImages, 
  FaBookOpen, 
  FaWhatsapp, 
  FaBirthdayCake, 
  FaSignOutAlt, 
  FaUserShield,
  FaBullhorn,
  FaUserCircle
} from 'react-icons/fa';
import Agenda from './Agenda';
import Discussion from './Discussion';
import LinkList from './LinkList';
import Script from './Script';
import DashboardManager from './DashboardManager';
import '../styles/Membres.css';

const EspaceMembres = () => {
  const { profile, deconnexion } = useAuth();
  
  // Hook appelé à L'INTÉRIEUR du composant avec l'option refetch
  const { items: posts, loading: loadingPosts, refetch: refetchPosts } = useCollection('dashboard_posts', 'created_at', true);
  
  const [nextBday, setNextBday] = useState(null);

  const [tab, setTab] = useState(() => {
    return localStorage.getItem('activeTab') || 'dashboard';
  });

  const handleTabChange = (newTab) => {
    setTab(newTab);
    localStorage.setItem('activeTab', newTab);
  };

  // Récupération dynamique des anniversaires depuis Supabase
  useEffect(() => {
    const fetchBirthdays = async () => {
      const { data } = await supabase.from('profiles').select('name, birthdate').not('birthdate', 'is', null);
      if (!data || data.length === 0) return;

      const today = new Date();
      const currentYear = today.getFullYear();

      const upcoming = data.map((m) => {
        const [, month, day] = m.birthdate.split('-').map(Number);
        let nextDate = new Date(currentYear, month - 1, day);
        if (nextDate < new Date(today.getFullYear(), today.getMonth(), today.getDate())) {
          nextDate = new Date(currentYear + 1, month - 1, day);
        }
        const diffDays = Math.ceil((nextDate - today) / (1000 * 60 * 60 * 24));
        return { name: m.name, day, month, diffDays };
      }).sort((a, b) => a.diffDays - b.diffDays);

      if (upcoming.length > 0) setNextBday(upcoming[0]);
    };

    fetchBirthdays();
  }, []);

  const isDirectorOrAdmin = profile?.is_admin || profile?.role === 'director';

  const getRoleBadge = (role) => {
    if (role === 'admin') return '👑 Admin';
    if (role === 'director') return '🎬 Metteur en scène';
    return '🎭 Comédien';
  };

  return (
    <div className="membres-container membres-dashboard">
      {/* HEADER DE L'ESPACE MEMBRES */}
      <div className="membres-header">
        <div>
          <h2>🎭 Espace Troupe — {profile?.name || 'Comédien(ne)'}</h2>
          <p className="subtext">
            Bienvenue ({getRoleBadge(profile?.role)})
          </p>
        </div>
        <div className="header-actions">
          <Link to="/profil" className="membres-btn">
            <FaUserCircle /> Mon Profil
          </Link>
          {profile?.is_admin && (
            <Link to="/admin-membres" className="membres-btn admin-btn">
              <FaUserShield /> Admin
            </Link>
          )}
          <button className="membres-btn logout-btn" onClick={deconnexion}>
            <FaSignOutAlt /> Déconnexion
          </button>
        </div>
      </div>

      {/* RACCOURCIS RAPIDES & ANNONCES */}
      <div className="membres-widgets">
        <a 
          href="https://chat.whatsapp.com/JNVs7kmnYFE76pFvXyTUjz" 
          target="_blank" 
          rel="noopener noreferrer" 
          className="widget-card whatsapp-widget"
        >
          <FaWhatsapp className="widget-icon" />
          <div>
            <strong>Groupe WhatsApp Troupe</strong>
            <p>Accéder aux discussions en direct</p>
          </div>
        </a>

        <div className="widget-card bday-widget">
          <FaBirthdayCake className="widget-icon" />
          <div>
            <strong>Prochain Anniversaire 🎂</strong>
            <p>
              {!nextBday
                ? "Aucun anniversaire renseigné"
                : nextBday.diffDays === 0
                ? `C'est l'anniversaire de ${nextBday.name} aujourd'hui ! 🎉`
                : `${nextBday.name} (${nextBday.day}/${nextBday.month}) dans ${nextBday.diffDays} jours`}
            </p>
          </div>
        </div>
      </div>

      {/* NAVIGATION PAR ONGLETS */}
      <nav className="membres-tabs">
        <button
          className={`membres-tab ${tab === 'dashboard' ? 'active' : ''}`}
          onClick={() => handleTabChange('dashboard')}
        >
          <FaBullhorn /> Tableau de bord
        </button>
        <button
          className={`membres-tab ${tab === 'agenda' ? 'active' : ''}`}
          onClick={() => handleTabChange('agenda')}
        >
          <FaCalendarAlt /> Agenda & Répétitions
        </button>
        <button
          className={`membres-tab ${tab === 'script' ? 'active' : ''}`}
          onClick={() => handleTabChange('script')}
        >
          <FaBookOpen /> Cahier de Metteuse en Scène
        </button>
        <button
          className={`membres-tab ${tab === 'videos' ? 'active' : ''}`}
          onClick={() => handleTabChange('videos')}
        >
          <FaVideo /> Vidéos Filages
        </button>
        <button
          className={`membres-tab ${tab === 'galerie' ? 'active' : ''}`}
          onClick={() => handleTabChange('galerie')}
        >
          <FaImages /> Photos & Coulisses
        </button>
        <button
          className={`membres-tab ${tab === 'discussion' ? 'active' : ''}`}
          onClick={() => handleTabChange('discussion')}
        >
          <FaComments /> Fil interne
        </button>
      </nav>

      {/* CONTENU SELON L'ONGLET */}
      <div className="tab-content">
        {tab === 'dashboard' && (
          <div className="membres-section">
            <h3>📢 Actualités & Annonces de la Troupe</h3>

            {loadingPosts ? (
              <p>Chargement des annonces...</p>
            ) : posts.length === 0 ? (
              <p style={{ fontStyle: 'italic', opacity: 0.7 }}>Aucune annonce particulière pour le moment.</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                {posts
                  .sort((a, b) => (b.is_pinned ? 1 : 0) - (a.is_pinned ? 1 : 0))
                  .map((post) => (
                    <div 
                      key={post.id} 
                      className="info-box"
                      style={{
                        borderLeft: post.category === 'urgent' ? '5px solid #dc3545' : post.is_pinned ? '5px solid #f1c40f' : '5px solid #007bff'
                      }}
                    >
                      <h4 style={{ margin: '0 0 8px 0' }}>
                        {post.is_pinned && '📌 '}{post.title}
                      </h4>
                      <p style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{post.content}</p>
                      <small style={{ opacity: 0.7, marginTop: '8px', display: 'block' }}>
                        Publié par {post.author} le {new Date(post.created_at).toLocaleDateString('fr-FR')}
                      </small>
                    </div>
                  ))}
              </div>
            )}

            {/* Outil d'administration avec passage du prop onRefresh */}
            {isDirectorOrAdmin && (
              <DashboardManager 
                posts={posts} 
                loading={loadingPosts} 
                onRefresh={refetchPosts} 
              />
            )}
          </div>
        )}

        {tab === 'agenda' && <Agenda />}
        {tab === 'discussion' && <Discussion />}
        {tab === 'videos' && (
          <LinkList
            collectionName="videos"
            label="📹 Vidéos & Enregistrements des filages"
            placeholder="Lien de la vidéo (YouTube non répertorié, Google Drive...)"
          />
        )}
        {tab === 'galerie' && (
          <LinkList
            collectionName="galerie"
            label="📸 Galerie photos & Coulisses de la troupe"
            placeholder="Lien de l'album photo partagé"
          />
        )}
        {tab === 'script' && <Script />}
      </div>
    </div>
  );
};

export default EspaceMembres;